/**
 * Issue #457: shared "try precomputed, fall back to JIT" policy used by all four manual
 * match-suggestion entry points — receipts (MarkAsMatchedModal), receivables and sales reports
 * (MarkAsReceivedModal), and the Bank/Transactions page (MatchSuggestionsModal).
 *
 * `forceRefresh=false` (the initial load) tries `getPrecomputed` first — the async match_jobs
 * pipeline's already-computed auto_match_decisions suggestion. A precomputed result of `[]`, or
 * the precomputed call itself throwing, is treated the same way: "nothing usable has been
 * computed yet" (the throw is logged, not surfaced to the user) — either way this falls through
 * to `getJit`, the pre-existing live scoring query, so behavior for "nothing computed yet"
 * matches what these screens already did before this policy existed.
 *
 * `forceRefresh=true` (the explicit "Search again" button on all four screens) always calls
 * `getJit` directly, bypassing precomputed entirely — this is how a transaction too new for the
 * ~1-minute match-job cron to have scored yet can still be found.
 */
export async function fetchWithPrecomputedFallback<T>(
  getPrecomputed: () => Promise<T[]>,
  getJit: () => Promise<T[]>,
  forceRefresh: boolean,
): Promise<T[]> {
  if (!forceRefresh) {
    try {
      const precomputed = await getPrecomputed();
      if (precomputed.length > 0) {
        return precomputed;
      }
    } catch (err) {
      console.error("Failed to fetch precomputed match suggestions:", err);
    }
  }
  return getJit();
}

/**
 * Settles a suggestion-fetch promise into a modal's loading/suggestions/loadFailed state —
 * identical across every "match to transaction" modal (payables, receivables, receipts, issue
 * #498). A failed fetch must never be conflated with a genuinely empty result (`.claude/rules/
 * client-error-handling.md`'s Tier 2 pattern) — `setLoadFailed(true)` is tracked separately from
 * `setSuggestions([])` so the two render distinct UI (see each modal's own finding-3 note).
 */
export function settleSuggestionFetch<T>(
  fetchPromise: Promise<T[]>,
  setters: {
    setSuggestions: (items: T[]) => void;
    setLoadFailed: (failed: boolean) => void;
    setLoading: (loading: boolean) => void;
  },
  errorContext: string,
): void {
  fetchPromise
    .then(setters.setSuggestions)
    .catch((err) => {
      console.error(errorContext, err);
      setters.setSuggestions([]);
      setters.setLoadFailed(true);
    })
    .finally(() => setters.setLoading(false));
}
